import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { DISPLAY_LIMITS, useDisplayPreferences } from '../display/display-preferences.ts';

export interface WorkspaceShellProps {
  title: string;
  subtitle: string;
  sidebar: ReactNode;
  headerContent?: ReactNode;
  headerActions?: ReactNode;
  mobileTaskLayout?: boolean;
  children: ReactNode;
}

export function WorkspaceShell({
  title,
  subtitle,
  sidebar,
  headerContent,
  headerActions,
  mobileTaskLayout = false,
  children,
}: WorkspaceShellProps): JSX.Element {
  const { preferences, updatePreferences } = useDisplayPreferences();

  const startSidebarResize = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    if (window.matchMedia('(max-width: 1023px)').matches) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = preferences.sidebarWidth;
    const handleMove = (moveEvent: PointerEvent): void => {
      const nextWidth = clamp(
        startWidth + moveEvent.clientX - startX,
        DISPLAY_LIMITS.sidebarWidth.min,
        DISPLAY_LIMITS.sidebarWidth.max,
      );
      updatePreferences({ sidebarWidth: nextWidth });
    };
    const handleUp = (): void => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp, { once: true });
  };

  return (
    <div className="workspace-shell">
      {sidebar}
      <button
        type="button"
        aria-label="Resize sidebar"
        data-testid="sidebar-resize-handle"
        onPointerDown={startSidebarResize}
        className="workspace-resize-handle hidden lg:block"
      />
      <div className="workspace-main">
        <header className={`workspace-header ${mobileTaskLayout ? 'max-md:hidden' : ''}`}>
          <div className="sr-only">
            <p>Portfolio PM</p>
            <h1 data-testid="workspace-heading">{title}</h1>
            <p data-testid="workspace-subheading">{subtitle}</p>
          </div>
          {headerContent ?? (
            <>
              <div className="min-w-0">
                <p className="eyebrow-label">Portfolio PM</p>
                <h1 className="mt-1 text-xl font-semibold tracking-tight text-[color:var(--app-text)]">
                  {title}
                </h1>
                <p className="mt-1 text-sm text-[color:var(--app-text-muted)]">{subtitle}</p>
              </div>
              {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
            </>
          )}
        </header>
        <div className={`workspace-body ${mobileTaskLayout ? 'max-md:p-0' : ''}`}>{children}</div>
      </div>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}
